import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuthStore } from '../auth/useAuthStore';
import { clientPortalApi, type PortalConfig } from './api';

export type PortalAccessStatus = 'unauthenticated' | 'checking' | 'granted' | 'denied';

interface PortalAccessValue {
  slug: string;
  config: PortalConfig | null;
  status: PortalAccessStatus;
}

const PortalAccessContext = createContext<PortalAccessValue | null>(null);

/**
 * Resuelve una sola vez, por cambio de `token`, si el usuario autenticado
 * tiene la entitlement del portal — expuesto a toda la subárbol del
 * portal vía contexto para no repetir el fetch en cada ruta.
 */
export function PortalAccessProvider({
  slug,
  children,
}: {
  slug: string;
  children: ReactNode;
}): JSX.Element {
  const token = useAuthStore(state => state.token);
  const [config, setConfig] = useState<PortalConfig | null>(null);
  const [status, setStatus] = useState<PortalAccessStatus>(token ? 'checking' : 'unauthenticated');

  useEffect(() => {
    let cancelled = false;
    clientPortalApi
      .getConfig(slug)
      .then(result => {
        if (!cancelled) setConfig(result);
      })
      .catch(() => {
        // Branding no disponible — el portal sigue funcionando sin ella.
      });
    return (): void => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    let cancelled = false;

    if (!token) {
      setStatus('unauthenticated');
      return;
    }

    setStatus('checking');
    clientPortalApi
      .checkAccess(slug, token)
      .then(granted => {
        if (!cancelled) setStatus(granted ? 'granted' : 'denied');
      })
      .catch(() => {
        if (!cancelled) setStatus('denied');
      });

    return (): void => {
      cancelled = true;
    };
  }, [slug, token]);

  return (
    <PortalAccessContext.Provider value={{ slug, config, status }}>
      {children}
    </PortalAccessContext.Provider>
  );
}

export function usePortalAccess(): PortalAccessValue {
  const ctx = useContext(PortalAccessContext);
  if (!ctx) {
    throw new Error('usePortalAccess debe usarse dentro de PortalAccessProvider');
  }
  return ctx;
}
